import React, { useState, useEffect } from 'react'
import { ASSIGNABLE_ROLES, LEAST_PRIVILEGED_ROLE, ROLE_LABELS } from '../utils/roles'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import {
  getSSOConfig,
  createSSOConfig,
  updateSSOConfig,
  deleteSSOConfig,
  testSSOConnection,
  expressSSOConnect,
  expressSSOAdminConsent,
  expressSSODisconnect,
} from '../services/api'
import { errorText } from '../utils/errorText'
import { dateTime } from '../utils/date'

interface SSOConfigData {
  id: string
  protocol: string
  idp_hint: string | null
  display_name: string | null
  // Express (Microsoft) mode
  sso_mode: string
  azure_tenant_id: string | null
  azure_tenant_name: string | null
  connected_by: string | null
  connected_at: string | null
  // OIDC
  issuer_url: string | null
  client_id: string | null
  oidc_scopes: string | null
  // SAML
  idp_entity_id: string | null
  idp_sso_url: string | null
  sp_entity_id: string | null
  idp_metadata_url: string | null
  has_idp_certificate: boolean
  // Common
  domain_mappings: string[]
  enforcement_mode: string
  is_enabled: boolean
  jit_enabled: boolean
  jit_default_role: string
  last_tested: string | null
  last_test_error: string | null
  created_at: string
  updated_at: string
}

// Least authority first. This is the role every person the identity provider has never introduced
// is created as, so it decides what a stranger can do on their first sign-in.
const ROLE_OPTIONS = ASSIGNABLE_ROLES.map((value) => ({ value, label: ROLE_LABELS[value] }))

const IDP_HINTS = [
  { value: '', label: 'None' },
  { value: 'azure_ad', label: 'Azure AD / Entra ID' },
  { value: 'google', label: 'Google Workspace' },
  { value: 'okta', label: 'Okta' },
  { value: 'other', label: 'Other' },
]

// IdP-specific setup instructions -- purely UX guidance
const IDP_INSTRUCTIONS: Record<string, Record<string, React.ReactNode>> = {
  azure_ad: {
    oidc: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Azure Portal &gt; Microsoft Entra ID &gt; App registrations &gt; New registration</strong></li>
        <li>Set <strong>Redirect URI</strong> to your SSO callback URL</li>
        <li>Under <strong>Certificates &amp; secrets</strong>, create a new client secret</li>
        <li>Copy the <strong>Application (client) ID</strong> as Client ID</li>
        <li>Set <strong>Issuer URL</strong> to: <code className="text-xs bg-theme-tertiary px-1 rounded">https://login.microsoftonline.com/YOUR_TENANT_ID/v2.0</code></li>
      </ol>
    ),
    saml: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Azure Portal &gt; Microsoft Entra ID &gt; Enterprise applications &gt; New application</strong></li>
        <li>Create a non-gallery application and go to <strong>Single sign-on &gt; SAML</strong></li>
        <li>Set <strong>Identifier (Entity ID)</strong> to your SP Entity ID</li>
        <li>Set <strong>Reply URL (ACS)</strong> to your SAML callback URL</li>
        <li>Download the <strong>Certificate (Base64)</strong> and copy <strong>Login URL</strong> and <strong>Azure AD Identifier</strong></li>
      </ol>
    ),
  },
  google: {
    oidc: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Google Cloud Console &gt; APIs &amp; Services &gt; Credentials</strong></li>
        <li>Click <strong>Create Credentials &gt; OAuth 2.0 Client ID</strong> (Web application)</li>
        <li>Add your callback URL to <strong>Authorized redirect URIs</strong></li>
        <li>Set <strong>Issuer URL</strong> to: <code className="text-xs bg-theme-tertiary px-1 rounded">https://accounts.google.com</code></li>
      </ol>
    ),
    saml: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Google Admin Console &gt; Apps &gt; Web and mobile apps &gt; Add app &gt; Add custom SAML app</strong></li>
        <li>Download the <strong>IdP metadata</strong> or copy SSO URL and certificate</li>
        <li>Set <strong>ACS URL</strong> to your SAML callback URL and <strong>Entity ID</strong> to your SP Entity ID</li>
      </ol>
    ),
  },
  okta: {
    oidc: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Okta Admin &gt; Applications &gt; Create App Integration</strong></li>
        <li>Select <strong>OIDC - OpenID Connect</strong> and <strong>Web Application</strong></li>
        <li>Set <strong>Sign-in redirect URI</strong> to your callback URL</li>
        <li>Set <strong>Issuer URL</strong> to: <code className="text-xs bg-theme-tertiary px-1 rounded">https://YOUR_DOMAIN.okta.com</code></li>
      </ol>
    ),
    saml: (
      <ol className="list-decimal list-inside space-y-2 text-sm text-theme-text-secondary">
        <li>Go to <strong>Okta Admin &gt; Applications &gt; Create App Integration</strong></li>
        <li>Select <strong>SAML 2.0</strong></li>
        <li>Set <strong>Single sign-on URL</strong> to your SAML callback URL</li>
        <li>Set <strong>Audience URI (SP Entity ID)</strong> to your SP Entity ID</li>
        <li>Download the IdP <strong>Signing Certificate</strong> and note the <strong>IdP SSO URL</strong></li>
      </ol>
    ),
  },
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-2 px-2 py-0.5 text-xs border border-theme-border-primary rounded hover:bg-theme-tertiary text-theme-text-secondary"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function SPMetadataBox({ protocol, tenantName }: { protocol: 'oidc' | 'saml'; tenantName?: string }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-domain.com'

  const oidcCallbackUrl = `${origin}/sso/callback`
  const samlAcsUrl = `${origin}/api/auth/sso/saml/callback`
  // Entity ID is tenant-specific so each tenant's IdP registration is distinct
  const samlEntityId = tenantName
    ? `${origin}/saml/${tenantName}`
    : `${origin}/saml/metadata`

  return (
    <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
      <h3 className="text-md font-semibold text-theme-text-primary mb-1">
        Service Provider Metadata
      </h3>
      <p className="text-xs text-theme-text-secondary mb-4">
        Copy these values into your Identity Provider's app registration.
        The callback URL is shared -- your tenant is identified from the login session.
      </p>

      {protocol === 'oidc' ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-theme-text-secondary mb-1">Redirect URI (Callback URL)</label>
            <div className="flex items-center">
              <code className="flex-1 text-sm bg-theme-tertiary px-3 py-1.5 rounded text-theme-text-primary font-mono break-all">{oidcCallbackUrl}</code>
              <CopyButton value={oidcCallbackUrl} />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-theme-text-secondary mb-1">Assertion Consumer Service (ACS) URL</label>
            <div className="flex items-center">
              <code className="flex-1 text-sm bg-theme-tertiary px-3 py-1.5 rounded text-theme-text-primary font-mono break-all">{samlAcsUrl}</code>
              <CopyButton value={samlAcsUrl} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-theme-text-secondary mb-1">SP Entity ID (Audience URI)</label>
            <div className="flex items-center">
              <code className="flex-1 text-sm bg-theme-tertiary px-3 py-1.5 rounded text-theme-text-primary font-mono break-all">{samlEntityId}</code>
              <CopyButton value={samlEntityId} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-theme-text-secondary mb-1">NameID Format</label>
            <code className="text-sm bg-theme-tertiary px-3 py-1.5 rounded text-theme-text-primary font-mono block">urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</code>
          </div>
        </div>
      )}
    </div>
  )
}

const SSOConfiguration: React.FC = () => {
  const { tenantContext } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [configData, setConfigData] = useState<{ configured: boolean; express_available?: boolean; config?: SSOConfigData } | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Which setup path is being shown while nothing is configured yet
  const [setupMode, setSetupMode] = useState<'express' | 'advanced'>('express')

  // Form state
  const [protocol, setProtocol] = useState<'oidc' | 'saml'>('oidc')
  const [idpHint, setIdpHint] = useState('')
  const [displayName, setDisplayName] = useState('')

  // OIDC fields
  const [issuerUrl, setIssuerUrl] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [oidcScopes, setOidcScopes] = useState('openid email profile')

  // SAML fields
  const [idpEntityId, setIdpEntityId] = useState('')
  const [idpSsoUrl, setIdpSsoUrl] = useState('')
  const [idpCertificate, setIdpCertificate] = useState('')
  const [spEntityId, setSpEntityId] = useState('')
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('')

  // Common fields
  const [domainInput, setDomainInput] = useState('')
  const [domainMappings, setDomainMappings] = useState<string[]>([])
  const [enforcementMode, setEnforcementMode] = useState('optional')
  const [jitEnabled, setJitEnabled] = useState(true)
  const [jitDefaultRole, setJitDefaultRole] = useState<string>(LEAST_PRIVILEGED_ROLE)

  const isConfigured = configData?.configured === true
  const expressAvailable = configData?.express_available === true
  const isExpress = isConfigured && configData?.config?.sso_mode === 'microsoft_express'

  // Derive default SP Entity ID from origin + tenant name
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const defaultSpEntityId = tenantContext?.tenant_name
    ? `${origin}/saml/${tenantContext.tenant_name}`
    : `${origin}/saml/metadata`

  useEffect(() => { loadConfig() }, [])

  // Results of the Connect / admin-consent redirects arrive as query parameters
  useEffect(() => {
    if (searchParams.get('express_connected') === '1') {
      setSuccessMessage('Microsoft SSO connected successfully')
      setSearchParams({}, { replace: true })
    } else if (searchParams.get('express_consent') === 'granted') {
      setSuccessMessage('Admin consent granted. Users can now sign in with Microsoft.')
      setSearchParams({}, { replace: true })
    } else if (searchParams.get('express_consent') === 'denied') {
      setError(searchParams.get('message') || 'Admin consent was not granted.')
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getSSOConfig()
      setConfigData(data)

      if (data.configured && data.config) {
        const c = data.config
        setSetupMode(c.sso_mode === 'microsoft_express' ? 'express' : 'advanced')
        setProtocol(c.protocol as 'oidc' | 'saml')
        setIdpHint(c.idp_hint || '')
        setDisplayName(c.display_name || '')
        setIssuerUrl(c.issuer_url || '')
        setClientId(c.client_id || '')
        setOidcScopes(c.oidc_scopes || 'openid email profile')
        setIdpEntityId(c.idp_entity_id || '')
        setIdpSsoUrl(c.idp_sso_url || '')
        setSpEntityId(c.sp_entity_id || '')
        setIdpMetadataUrl(c.idp_metadata_url || '')
        setDomainMappings(c.domain_mappings || [])
        setEnforcementMode(c.enforcement_mode)
        setJitEnabled(c.jit_enabled)
        setJitDefaultRole(c.jit_default_role)
      } else {
        // Pre-populate SP Entity ID for new SAML configs
        setSpEntityId(defaultSpEntityId)
      }
    } catch (err: any) {
      // Gracefully handle: endpoint not deployed, backend error, network issue
      const status = err.response?.status
      if (status === 404 || status === 500 || status === 502 || status === 503 || !err.response) {
        setConfigData({ configured: false })
      } else {
        setError(errorText(err, 'Failed to load SSO configuration'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddDomain = () => {
    const domain = domainInput.trim().toLowerCase()
    if (domain && !domainMappings.includes(domain)) {
      setDomainMappings([...domainMappings, domain])
      setDomainInput('')
    }
  }

  const handleExpressConnect = async () => {
    setError(null)
    setSuccessMessage(null)
    try {
      setConnecting(true)
      let result
      try {
        result = await expressSSOConnect(false)
      } catch (err: any) {
        if (err.response?.status === 409) {
          if (!confirm('An SSO configuration is already enabled. Connecting Microsoft SSO will disable it. Continue?')) {
            return
          }
          result = await expressSSOConnect(true)
        } else {
          throw err
        }
      }
      window.location.href = result.auth_url
    } catch (err: any) {
      setError(errorText(err, 'Failed to start the Microsoft connection'))
    } finally {
      setConnecting(false)
    }
  }

  const handleAdminConsent = async () => {
    setError(null)
    setSuccessMessage(null)
    try {
      const result = await expressSSOAdminConsent()
      window.location.href = result.consent_url
    } catch (err: any) {
      setError(errorText(err, 'Failed to start the admin consent flow'))
    }
  }

  const handleExpressDisconnect = async () => {
    if (!confirm('Disconnect Microsoft SSO? Users will no longer be able to sign in with Microsoft; password login stays available.')) return
    try {
      setError(null)
      await expressSSODisconnect()
      setSuccessMessage('Microsoft SSO disconnected')
      await loadConfig()
    } catch (err: any) {
      setError(errorText(err, 'Failed to disconnect Microsoft SSO'))
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (isExpress) {
      // Express keeps its credentials on our side; only the common settings are editable
      try {
        setSaving(true)
        await updateSSOConfig({
          domain_mappings: domainMappings,
          enforcement_mode: enforcementMode,
          jit_enabled: jitEnabled,
          jit_default_role: jitDefaultRole,
        })
        setSuccessMessage('SSO settings updated successfully')
        await loadConfig()
      } catch (err: any) {
        setError(errorText(err, 'Failed to save SSO settings'))
      } finally {
        setSaving(false)
      }
      return
    }

    if (protocol === 'oidc') {
      if (!issuerUrl) { setError('Issuer URL is required for OIDC'); return }
      if (!clientId) { setError('Client ID is required for OIDC'); return }
      if (!isConfigured && !clientSecret) { setError('Client Secret is required for OIDC'); return }
    } else {
      if (!idpSsoUrl && !idpMetadataUrl) { setError('IdP SSO URL or Metadata URL is required for SAML'); return }
    }

    try {
      setSaving(true)

      if (isConfigured) {
        const updateData: Record<string, any> = {
          display_name: displayName || null,
          idp_hint: idpHint || null,
          domain_mappings: domainMappings,
          enforcement_mode: enforcementMode,
          jit_enabled: jitEnabled,
          jit_default_role: jitDefaultRole,
        }
        if (protocol === 'oidc') {
          updateData.issuer_url = issuerUrl
          updateData.client_id = clientId
          updateData.oidc_scopes = oidcScopes
          if (clientSecret) updateData.client_secret = clientSecret
        } else {
          updateData.idp_entity_id = idpEntityId || null
          updateData.idp_sso_url = idpSsoUrl || null
          updateData.sp_entity_id = spEntityId || null
          updateData.idp_metadata_url = idpMetadataUrl || null
          if (idpCertificate) updateData.idp_certificate = idpCertificate
        }
        await updateSSOConfig(updateData)
        setSuccessMessage('SSO configuration updated successfully')
      } else {
        const createData: any = {
          protocol,
          idp_hint: idpHint || undefined,
          display_name: displayName || undefined,
          domain_mappings: domainMappings,
          enforcement_mode: enforcementMode,
          jit_enabled: jitEnabled,
          jit_default_role: jitDefaultRole,
        }
        if (protocol === 'oidc') {
          createData.issuer_url = issuerUrl
          createData.client_id = clientId
          createData.client_secret = clientSecret
          createData.oidc_scopes = oidcScopes
        } else {
          createData.idp_entity_id = idpEntityId || undefined
          createData.idp_sso_url = idpSsoUrl || undefined
          createData.idp_certificate = idpCertificate || undefined
          createData.sp_entity_id = spEntityId || undefined
          createData.idp_metadata_url = idpMetadataUrl || undefined
        }
        await createSSOConfig(createData)
        setSuccessMessage('SSO configuration created successfully')
      }

      await loadConfig()
      setClientSecret('')
      setIdpCertificate('')
    } catch (err: any) {
      setError(errorText(err, 'Failed to save SSO configuration'))
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setError(null)
    setSuccessMessage(null)
    try {
      setTesting(true)
      const result = await testSSOConnection()
      if (result.success) {
        setSuccessMessage('Connection test successful!')
        await loadConfig()
      } else {
        setError(result.error || 'Connection test failed')
      }
    } catch (err: any) {
      setError(errorText(err, 'Failed to test connection'))
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove SSO configuration? Users who rely on SSO will no longer be able to sign in.')) return
    try {
      setError(null)
      await deleteSSOConfig()
      setSuccessMessage('SSO configuration removed')
      await loadConfig()  // re-reads configured:false AND express_available for the chooser
      // Reset form
      setClientId(''); setClientSecret(''); setIssuerUrl(''); setOidcScopes('openid email profile')
      setIdpEntityId(''); setIdpSsoUrl(''); setIdpCertificate(''); setSpEntityId(''); setIdpMetadataUrl('')
      setDomainMappings([]); setEnforcementMode('optional')
      setJitEnabled(true); setJitDefaultRole(LEAST_PRIVILEGED_ROLE)
      setIdpHint(''); setDisplayName('')
    } catch (err: any) {
      setError(errorText(err, 'Failed to delete SSO configuration'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  const instructions = idpHint ? IDP_INSTRUCTIONS[idpHint]?.[protocol] : null

  // Which blocks are visible: the Express setup card, the Advanced (manual IdP) form, or the
  // common-settings form shown for a connected Express configuration.
  const showExpressSetup = !isConfigured && setupMode === 'express' && expressAvailable
  const showAdvancedForm = !isExpress && !showExpressSetup
  const showForm = isExpress || showAdvancedForm

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Link to="/settings/integrations" className="text-theme-text-secondary hover:text-theme-text-primary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h2 className="text-2xl font-bold text-theme-text-primary">Single Sign-On (SSO)</h2>
          <p className="text-theme-text-secondary text-sm mt-1">
            Configure OIDC or SAML-based SSO with automatic user provisioning
          </p>
        </div>
      </div>

      {/* Status banner */}
      {isConfigured && configData?.config && (
        <div className={`rounded-lg p-4 border ${
          configData.config.is_enabled
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2.5 h-2.5 rounded-full ${configData.config.is_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className="text-sm font-medium text-theme-text-primary">
                {configData.config.sso_mode === 'microsoft_express'
                  ? 'Microsoft Entra ID (Express)'
                  : configData.config.protocol.toUpperCase()}
                {configData.config.display_name ? ` — ${configData.config.display_name}` : ''}
                {' — '}{configData.config.is_enabled ? 'Active' : 'Disabled'}
              </span>
            </div>
            {configData.config.last_tested && (
              <span className="text-xs text-theme-text-secondary">
                Last tested: {dateTime(configData.config.last_tested)}
                {configData.config.last_test_error && <span className="text-red-500 ml-1">(failed)</span>}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-700 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Setup mode chooser, until something is configured */}
      {!isConfigured && expressAvailable && (
        <div className="grid grid-cols-2 gap-4">
          {([
            {
              value: 'express' as const,
              title: 'Microsoft Entra ID',
              badge: 'Recommended',
              body: 'One-click setup. No app registration needed — sign in with your work account to connect.',
            },
            {
              value: 'advanced' as const,
              title: 'Advanced (any IdP)',
              badge: null,
              body: 'Configure OIDC or SAML with your own app registration in Entra ID, Okta, Google, or another IdP.',
            },
          ]).map(card => (
            <button
              key={card.value}
              type="button"
              onClick={() => setSetupMode(card.value)}
              className={`text-left p-4 rounded-lg border-2 transition-colors ${
                setupMode === card.value
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-theme-border-primary bg-theme-secondary hover:border-blue-300'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-semibold text-theme-text-primary">{card.title}</span>
                {card.badge && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">{card.badge}</span>
                )}
              </div>
              <p className="text-xs text-theme-text-secondary">{card.body}</p>
            </button>
          ))}
        </div>
      )}

      {/* Express setup: connect with one click */}
      {showExpressSetup && (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 space-y-4">
          <h3 className="text-md font-semibold text-theme-text-primary">Connect Microsoft Entra ID</h3>
          <p className="text-sm text-theme-text-secondary">
            Sign in with a work or school account of the organization you want to connect. Only basic
            sign-in permissions are requested — nothing related to Intune or your directory. Users of
            your email domain can then sign in with Microsoft; accounts are created automatically on
            first sign-in.
          </p>
          <button
            type="button"
            onClick={handleExpressConnect}
            disabled={connecting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {connecting ? 'Redirecting to Microsoft...' : 'Connect Microsoft SSO'}
          </button>
          <p className="text-xs text-theme-text-secondary">
            If your organization blocks user consent for new applications, an administrator can use
            the admin consent option after the first connection attempt fails.
          </p>
        </div>
      )}

      {/* Express connected: connection details and actions */}
      {isExpress && configData?.config && (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 space-y-4">
          <h3 className="text-md font-semibold text-theme-text-primary">Microsoft Entra ID Connection</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs text-theme-text-secondary">Organization</dt>
              <dd className="text-theme-text-primary">{configData.config.azure_tenant_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-theme-text-secondary">Entra tenant ID</dt>
              <dd className="text-theme-text-primary font-mono text-xs">{configData.config.azure_tenant_id || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-theme-text-secondary">Connected by</dt>
              <dd className="text-theme-text-primary">{configData.config.connected_by || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-theme-text-secondary">Connected at</dt>
              <dd className="text-theme-text-primary">
                {configData.config.connected_at ? dateTime(configData.config.connected_at) : '—'}
              </dd>
            </div>
          </dl>
          <div className="flex items-center space-x-3 pt-2">
            {!configData.config.is_enabled && (
              <button
                type="button"
                onClick={handleExpressConnect}
                disabled={connecting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {connecting ? 'Redirecting to Microsoft...' : 'Reconnect'}
              </button>
            )}
            <button
              type="button"
              onClick={handleAdminConsent}
              className="px-4 py-2 border border-theme-border-primary text-theme-text-primary rounded-lg hover:bg-theme-tertiary text-sm font-medium"
            >
              Grant admin consent
            </button>
            {configData.config.is_enabled && (
              <button
                type="button"
                onClick={handleExpressDisconnect}
                className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium"
              >
                Disconnect
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium"
            >
              Remove SSO
            </button>
          </div>
          <p className="text-xs text-theme-text-secondary">
            "Grant admin consent" approves sign-in for every user at once — needed only when your
            organization blocks user consent for new applications. "Remove SSO" deletes this
            configuration entirely, so you can start over or switch to Advanced.
          </p>
        </div>
      )}

      {/* SP Metadata -- values to configure on the IdP side (Advanced only) */}
      {showAdvancedForm && (
        <SPMetadataBox protocol={protocol} tenantName={tenantContext?.tenant_name} />
      )}

      {showForm && (
      <form onSubmit={handleSave} className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 space-y-6">
        {/* Protocol selector */}
        {!isExpress && (
        <div>
          <label className="block text-sm font-medium text-theme-text-primary mb-2">Protocol</label>
          <div className="flex space-x-1 bg-theme-tertiary rounded-lg p-1">
            {(['oidc', 'saml'] as const).map(p => (
              <button
                key={p}
                type="button"
                disabled={isConfigured}
                onClick={() => setProtocol(p)}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  protocol === p
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-theme-text-secondary hover:text-theme-text-primary'
                } disabled:opacity-60`}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
          {isConfigured && (
            <p className="text-xs text-theme-text-secondary mt-1">Protocol cannot be changed. Delete and recreate to switch.</p>
          )}
        </div>
        )}

        {/* Display name + IdP hint */}
        {!isExpress && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-primary mb-1">Display Name <span className="text-theme-text-secondary font-normal">(optional)</span></label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. Corporate SSO"
              className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
            />
            <p className="text-xs text-theme-text-secondary mt-1">Shown on the login button</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text-primary mb-1">Identity Provider <span className="text-theme-text-secondary font-normal">(for instructions)</span></label>
            <select
              value={idpHint}
              onChange={e => setIdpHint(e.target.value)}
              className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
            >
              {IDP_HINTS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        )}

        {/* IdP-specific instructions (if hint selected) */}
        {!isExpress && instructions && (
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
              {IDP_HINTS.find(h => h.value === idpHint)?.label} — {protocol.toUpperCase()} Setup Guide
            </h4>
            {instructions}
          </div>
        )}

        {/* OIDC fields */}
        {!isExpress && protocol === 'oidc' && (
          <div className="space-y-4 border-t border-theme-border-primary pt-4">
            <h4 className="text-md font-semibold text-theme-text-primary">OIDC Configuration</h4>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">Issuer URL</label>
              <input
                type="url"
                value={issuerUrl}
                onChange={e => setIssuerUrl(e.target.value)}
                placeholder="https://login.microsoftonline.com/{tenant-id}/v2.0"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
              <p className="text-xs text-theme-text-secondary mt-1">The OIDC discovery document will be fetched from <code>{'{issuer}'}/.well-known/openid-configuration</code></p>
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">Client ID</label>
              <input
                type="text"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                placeholder="Application (client) ID"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">
                Client Secret {isConfigured && <span className="text-theme-text-secondary font-normal">(leave blank to keep existing)</span>}
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder={isConfigured ? '••••••••' : 'Client secret value'}
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">Scopes</label>
              <input
                type="text"
                value={oidcScopes}
                onChange={e => setOidcScopes(e.target.value)}
                placeholder="openid email profile"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
          </div>
        )}

        {/* SAML fields */}
        {!isExpress && protocol === 'saml' && (
          <div className="space-y-4 border-t border-theme-border-primary pt-4">
            <h4 className="text-md font-semibold text-theme-text-primary">SAML Configuration</h4>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">IdP Metadata URL <span className="text-theme-text-secondary font-normal">(optional)</span></label>
              <input
                type="url"
                value={idpMetadataUrl}
                onChange={e => setIdpMetadataUrl(e.target.value)}
                placeholder="https://idp.example.com/metadata"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
              <p className="text-xs text-theme-text-secondary mt-1">If provided, IdP settings can be auto-configured from metadata</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">IdP SSO URL</label>
              <input
                type="url"
                value={idpSsoUrl}
                onChange={e => setIdpSsoUrl(e.target.value)}
                placeholder="https://idp.example.com/sso/saml"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">IdP Entity ID</label>
              <input
                type="text"
                value={idpEntityId}
                onChange={e => setIdpEntityId(e.target.value)}
                placeholder="https://sts.windows.net/{tenant-id}/"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">SP Entity ID <span className="text-theme-text-secondary font-normal">(our identifier)</span></label>
              <input
                type="text"
                value={spEntityId}
                onChange={e => setSpEntityId(e.target.value)}
                placeholder="https://your-app.com/saml/metadata"
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-primary mb-1">
                IdP X.509 Certificate (PEM) {isConfigured && configData?.config?.has_idp_certificate && (
                  <span className="text-theme-text-secondary font-normal">(leave blank to keep existing)</span>
                )}
              </label>
              <textarea
                value={idpCertificate}
                onChange={e => setIdpCertificate(e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                rows={4}
                className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary font-mono text-xs"
              />
            </div>
          </div>
        )}

        {/* Domain Mappings */}
        <div className="border-t border-theme-border-primary pt-6">
          <label className="block text-sm font-medium text-theme-text-primary mb-1">Email Domains <span className="text-theme-text-secondary font-normal">(optional)</span></label>
          <p className="text-xs text-theme-text-secondary mb-2">Restrict SSO to specific email domains. Leave empty to allow any email domain.</p>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={domainInput}
              onChange={e => setDomainInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain() } }}
              placeholder="example.com"
              className="flex-1 px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
            />
            <button type="button" onClick={handleAddDomain} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {domainMappings.map(domain => (
              <span key={domain} className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                {domain}
                <button type="button" onClick={() => setDomainMappings(domainMappings.filter(d => d !== domain))} className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800">&times;</button>
              </span>
            ))}
          </div>
        </div>

        {/* Enforcement Mode */}
        <div>
          <label className="block text-sm font-medium text-theme-text-primary mb-2">Enforcement Mode</label>
          <div className="space-y-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input type="radio" name="enforcement" value="optional" checked={enforcementMode === 'optional'} onChange={e => setEnforcementMode(e.target.value)} className="mt-1" />
              <div>
                <div className="text-sm font-medium text-theme-text-primary">Optional</div>
                <div className="text-xs text-theme-text-secondary">Users can sign in with SSO or password</div>
              </div>
            </label>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input type="radio" name="enforcement" value="required" checked={enforcementMode === 'required'} onChange={e => setEnforcementMode(e.target.value)} className="mt-1" />
              <div>
                <div className="text-sm font-medium text-theme-text-primary">Required</div>
                <div className="text-xs text-theme-text-secondary">Password login disabled for users in mapped domains</div>
              </div>
            </label>
          </div>
        </div>

        {/* JIT Provisioning */}
        <div className="border-t border-theme-border-primary pt-6">
          <h4 className="text-md font-semibold text-theme-text-primary mb-4">Just-In-Time Provisioning</h4>
          <div className="space-y-4">
            <label className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-theme-text-primary">Enable JIT Provisioning</div>
                <div className="text-xs text-theme-text-secondary">Create an account and add it to this organization on first SSO sign-in</div>
              </div>
              <button type="button" onClick={() => setJitEnabled(!jitEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${jitEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${jitEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
            {jitEnabled ? (
              <div>
                <label className="block text-sm font-medium text-theme-text-primary mb-1">Default Role</label>
                <select value={jitDefaultRole} onChange={e => setJitDefaultRole(e.target.value)} className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary">
                  {ROLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            ) : (
              <p data-testid="jit-off-note" className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                With JIT provisioning off, only people you invite can sign in through SSO. Invite each of them in{' '}
                <Link to="/teams/users" className="font-medium underline">Users</Link> with the sign-in method set to
                single sign-on; their account is created with the invited role when they first sign in. Anyone else
                is turned away after authenticating, and no account is created for them.
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between border-t border-theme-border-primary pt-6">
          <div className="flex space-x-3">
            <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              {saving ? 'Saving...' : isConfigured ? (isExpress ? 'Update Settings' : 'Update Configuration') : 'Save Configuration'}
            </button>
            {isConfigured && (
              <button type="button" onClick={handleTest} disabled={testing} className="px-4 py-2 border border-theme-border-primary text-theme-text-primary rounded-lg hover:bg-theme-tertiary disabled:opacity-50 text-sm font-medium">
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            )}
          </div>
          {isConfigured && !isExpress && (
            <button type="button" onClick={handleDelete} className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium">
              Remove SSO
            </button>
          )}
        </div>
      </form>
      )}
    </div>
  )
}

export default SSOConfiguration
